import React, { useState } from 'react';
import { useTeamsStore } from '@/stores/teamsStore';
import { Link, useNavigate } from 'react-router-dom';
import { useReadOnlyMode } from '@nimbitlabs/features';
import { PermissionGate } from '@nimbitlabs/features';
import type { Team } from '@/types/team';
import { Alert, AlertDescription, AlertTitle, Avatar, AvatarFallback, AvatarImage, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input } from '@nimbitlabs/ui';
import { 
  Search, 
  Plus, 
  Users, 
  Package,
  Mail,
  MessageSquare,
  UserPlus,
  Lock,
  Info,
  Trash2
} from 'lucide-react';

export const Teams: React.FC = () => {
  const navigate = useNavigate();
  const { teams, loading, deleteTeam: deleteTeamFn } = useTeamsStore();
  const { isReadOnly } = useReadOnlyMode();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  
  const deleteTeam = async (id: string) => {
    await deleteTeamFn(id);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Lead':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'Developer':
        return 'bg-info/10 text-info border-info/20';
      case 'DevOps':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'Designer':
        return 'bg-success/10 text-success border-success/20';
      case 'QA':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  // Filter teams based on search query
  const filteredTeams = React.useMemo(() => {
    return teams
      .filter(team => {
        const q = searchQuery.toLowerCase();
        return (
          team.name.toLowerCase().includes(q) ||
          team.description.toLowerCase().includes(q) ||
          (team.tags ?? []).some(tag => tag.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => {
        // Sort unlocked first, then locked
        if (a.isLocked === b.isLocked) return 0;
        return a.isLocked ? 1 : -1;
      });
  }, [teams, searchQuery]);

  return (
    <div className="space-y-3 md:space-y-6 px-2 md:px-0">
      {/* Header Section */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Teams</h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Manage your teams and their service ownership
          </p>
        </div>
        <PermissionGate permission="teams:create">
          <Button 
            onClick={() => navigate('/teams/new')} 
            className="w-fit"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Team
          </Button>
        </PermissionGate>
      </div>

      {/* Locked Resources Alert */}
      {teams.some(t => t.isLocked) && (
        <Alert className="border border-warning/15 bg-warning/5">
          <Info className="h-4 w-4 text-warning" />
          <AlertTitle className="text-warning">Some teams are locked</AlertTitle>
          <AlertDescription>
            {teams.filter(t => t.isLocked).length} team(s) are locked due to subscription limits. 
            Your data is safe. Delete teams to free up quota or upgrade your plan to unlock them.
          </AlertDescription>
        </Alert>
      )}

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search teams by name, description, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Teams Grid */}
      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-muted rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-5 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="h-4 bg-muted rounded" />
                <div className="h-4 bg-muted rounded w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredTeams.map((team, index) => (
          <Card 
            key={team.id} 
            className={`group relative overflow-hidden border-border/40 bg-card/50 hover:shadow-lg hover:border-primary/30 hover:bg-card transition-all duration-300 animate-fade-in ${
              team.isLocked ? 'opacity-60' : 'cursor-pointer'
            }`}
            style={{ animationDelay: `${index * 50}ms` }}
            onClick={() => !team.isLocked && navigate(`/teams/${team.id}`)}
          >
            <CardHeader className="pb-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-lg bg-primary/5 border border-primary/10">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <CardTitle className="text-xl font-bold truncate">{team.name}</CardTitle>
                    <Badge variant="outline" className="bg-info/10 text-info border-info/30">
                      <Users className="h-3 w-3 mr-1" />
                      {team.members.length}
                    </Badge>
                    {team.isLocked && (
                      <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 flex-shrink-0">
                        <Lock className="h-3 w-3 mr-1" />
                        Locked
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="line-clamp-2 text-sm">
                    {team.description}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 pt-0">
              {/* Services */}
              <div className="py-3 border-y border-border/40">
                <div className="flex items-center gap-1.5 mb-2">
                  <Package className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Services
                  </span>
                </div>
                {team.services && team.services.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {team.services.slice(0, 4).map((service: any) => {
                      const initials = service.name
                        .split(/[\s-_]+/)
                        .map((word: string) => word[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 3);
                      
                      return (
                        <Badge 
                          key={service.id} 
                          variant="outline" 
                          className="bg-primary/10 text-primary border-primary/30 text-xs font-mono"
                          title={service.name}
                        >
                          {initials}
                        </Badge>
                      );
                    })}
                    {team.services.length > 4 && (
                      <Badge variant="outline" className="bg-muted text-muted-foreground text-xs">
                        +{team.services.length - 4}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <Badge variant="outline" className="bg-success/10 text-success border-success/30 font-semibold">
                    {team.servicesCount || 0}
                  </Badge>
                )}
              </div>

              {/* Team Members Preview */}
              {team.members.length > 0 && (
                <div className="flex -space-x-2 py-2">
                  {team.members.slice(0, 4).map((member, idx) => {
                    const words = member.name.trim().split(/\s+/);
                    const initials = words.length > 1 
                      ? (words[0][0] + words[1][0]).toUpperCase()
                      : member.name.slice(0, 2).toUpperCase();
                    
                    return (
                      <Avatar 
                        key={idx} 
                        className="h-7 w-7 border-2 border-background"
                        title={`${member.name} - ${member.role}`}
                      >
                        <AvatarImage src={member.avatar} alt={member.name} />
                        <AvatarFallback className="text-xs bg-primary/10">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                    );
                  })}
                  {team.members.length > 4 && (
                    <div className="h-7 w-7 rounded-full bg-muted border-2 border-background flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">
                        +{team.members.length - 4}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {team.tags.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Tags
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {team.tags.slice(0, 3).map((tag) => (
                      <Badge 
                        key={tag} 
                        variant="outline" 
                        className="text-xs bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchQuery(tag);
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                    {team.tags.length > 3 && (
                      <Badge variant="outline" className="text-xs bg-secondary/30">
                        +{team.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex gap-1.5">
                  {team.email && (
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                      className="h-9 w-9 p-0 rounded-lg hover:bg-blue-500/10 hover:text-blue-500 transition-all group/btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <a href={`mailto:${team.email}`} title="Email team">
                        <Mail className="h-4 w-4 group-hover/btn:rotate-12 transition-transform" />
                      </a>
                    </Button>
                  )}
                  <PermissionGate permission="teams:delete">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (confirm(`Are you sure you want to delete "${team.name}"?`)) {
                          try {
                            await deleteTeam(team.id);
                          } catch (error) {
                            // Error handled by store
                          }
                        }
                      }}
                      className="h-9 w-9 p-0 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-all group/btn"
                      title="Delete team"
                    >
                      <Trash2 className="h-4 w-4 group-hover/btn:rotate-12 transition-transform" />
                    </Button>
                  </PermissionGate>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={team.isLocked}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!team.isLocked) {
                      navigate(`/teams/${team.id}`);
                    }
                  }}
                  className="font-medium bg-blue-500/10 text-blue-600 border-blue-500/30 hover:bg-blue-500/20 hover:border-blue-500/40 transition-all"
                >
                  View Details
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {!loading && filteredTeams.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No teams found</h3>
            <p className="text-muted-foreground">
              {searchQuery ? 'Try adjusting your search criteria.' : 'Get started by creating your first team using the button above.'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
